import { Parcelle, NatureMutation } from 'interfaces/Dvf'
import { useAppSelector } from 'redux/hooks'
import { calculBounds } from 'utils/calculBounds'
import geoJsonObjectToCoordinates from 'utils/geoJsonObjectToCoordinates'
import { ReactComponent as PictoCalendrier } from 'assets/default/svg/picto_calendrier.svg'
import { ReactComponent as PictoEuro } from 'assets/default/svg/picto_euro.svg'
import { ReactComponent as PictoNatureMutation } from 'assets/default/svg/picto_nature_mutation.svg'
import { ReactComponent as PictoNbPiece } from 'assets/default/svg/picto_nb_pieces.svg'
import { ReactComponent as PictoSurface } from 'assets/default/svg/picto_surface.svg'
import { ReactComponent as PictoTerrainEtudierMarche } from 'assets/default/svg/picto_terrain_etudier_marche.svg'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import { useState } from 'react'
import { Badge, Button } from 'react-bootstrap'
import useGoogleMap from 'hooks/useGoogleMap'
import {
  makeSelectMutations,
  makeSelectSelectedParcelleIndex,
} from 'redux/features/dvf/selectors'
import Card from '../Card'
import getFormattedLocalType from './utils/getFormattedLocalType'
import getNatureCultureName from './utils/getNatureCultureName'
import s from './CardDVFResult.module.scss'
import cardStyle from '../Card.module.scss'

interface Props {
  parcelleDvf: Parcelle
  onClick: (uid: string) => void
  count?: {
    index: number
    total: number
  }
}

const defaultProps = {
  count: undefined,
}

const LibelleNatureMutation: { [natureMutation: string]: string } = {
  [NatureMutation.Vente]: 'Vente',
  [NatureMutation.VenteFuturAchevement]: 'Vente futur achèvement',
  [NatureMutation.VenteTerrainBatir]: 'Vente terrain à bâtir',
  [NatureMutation.Adjudication]: 'Adjudication',
  [NatureMutation.Expropriation]: 'Expropriation',
  [NatureMutation.Echange]: 'Échange',
}

const CardDVFResult = ({
  parcelleDvf,
  onClick,
  count,
}: Props): JSX.Element | null => {
  const map = useGoogleMap()
  const [mutationIdx, setMutationIdx] = useState(0)
  const mutations = useAppSelector(makeSelectMutations)
  const selectedParcelleIndex = useAppSelector(makeSelectSelectedParcelleIndex)
  // Cannot be undefined because this component needs dvf to render
  const { parcelleUid, parcelleAdresse, parcelleGeography, idsMutation } =
    parcelleDvf
  const handleCardClick = () => {
    const bounds = calculBounds(geoJsonObjectToCoordinates(parcelleGeography))
    map?.fitBounds(bounds)
    map?.panToBounds(bounds)
    if (onClick) onClick(parcelleUid)
  }

  const nextMutation = () =>
    setMutationIdx((prev) => (prev > 0 ? prev - 1 : prev))

  const previousMutation = () =>
    setMutationIdx((prev) => (prev < idsMutation.length - 1 ? prev + 1 : prev))

  const idMutation = idsMutation[mutationIdx]
  const mutation = mutations[idMutation]

  if (!mutation) {
    console.error(
      `Parcelle ${parcelleUid} is missing its mutation ${idMutation}`
    )
    return null
  }

  const {
    prixM2,
    date,
    montant,
    natureMutation,
    multiParcelle,
    local,
    terrain,
    adressesParcelle,
  } = mutation

  let address = parcelleAdresse

  if (!address) {
    const { voirie, commune } = adressesParcelle[parcelleUid]
    address = `${voirie} ${commune}`
  }

  if (!address) {
    console.error(`Parcelle ${parcelleUid} is missing its address`)
  }

  return (
    <Card
      className={s.cardDVF}
      onClick={handleCardClick}
      selected={
        selectedParcelleIndex !== null && selectedParcelleIndex === count?.index
      }
    >
      <Card.Header>
        <Card.Title>{address}</Card.Title>
        <Card.Badge>
          <Badge
            bg="primary-lighten-5"
            className={s.badge}
          >{`${prixM2} €/m²`}</Badge>
        </Card.Badge>
      </Card.Header>
      <Card.Body>
        <ul className={s.detailList}>
          <li>
            {new Date(date).toLocaleDateString()} <PictoCalendrier />
          </li>
          <li>
            {montant} <PictoEuro />
          </li>
          {natureMutation && (
            <li>
              {LibelleNatureMutation[natureMutation]} <PictoNatureMutation />
            </li>
          )}
        </ul>

        {multiParcelle && (
          <p className={s.multiDescription}>
            Cette mutation contient des dispositions dans des parcelles
            adjacentes.
            <br />
            La valeur foncière correspond au total.
          </p>
        )}

        {local.length > 0 && (
          <>
            <p className={s.subtitle}>Locaux</p>
            <ul className={s.itemList}>
              {local.map(({ type, nbPiece, surface }, localIndex) => (
                <li key={localIndex}>
                  <ul className={s.detailList}>
                    <li>{getFormattedLocalType(type)}</li>
                    {nbPiece && (
                      <li>
                        {nbPiece} pièces <PictoNbPiece />
                      </li>
                    )}
                    {surface && (
                      <li>
                        {surface} m² <PictoSurface />
                      </li>
                    )}
                  </ul>
                </li>
              ))}
            </ul>
          </>
        )}

        {terrain.length > 0 && (
          <>
            <p className={s.subtitle}>Terrains</p>
            <ul className={s.itemList}>
              {terrain.map(({ natureCulture, surface }, terrainIndex) => (
                <li key={terrainIndex}>
                  <ul className={s.detailList}>
                    <li>
                      {getNatureCultureName(natureCulture)}
                      <PictoTerrainEtudierMarche />
                    </li>
                    {surface && (
                      <li>
                        {surface} m² <PictoSurface />
                      </li>
                    )}
                  </ul>
                </li>
              ))}
            </ul>
          </>
        )}
        {idsMutation.length > 1 && (
          <div className={s.navigation}>
            <div className={s.previousMutation}>
              <Button
                variant="link"
                onClick={nextMutation}
                disabled={mutationIdx === 0}
              >
                <FaChevronLeft title="Mutation précédente" />
              </Button>
            </div>
            <div className={s.currentMutation}>
              <span>
                {mutationIdx + 1}/{idsMutation.length}
              </span>
            </div>
            <div className={s.nextMutation}>
              <Button
                variant="link"
                onClick={previousMutation}
                disabled={mutationIdx === idsMutation.length - 1}
              >
                <FaChevronRight title="Mutation suivante" />
              </Button>
            </div>
          </div>
        )}
        {count && (
          <div className={cardStyle.cardCount}>
            {`${count.index + 1} sur ${count.total}`}
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

CardDVFResult.defaultProps = defaultProps
export default CardDVFResult
